// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import Truck from "@insite/mobius/Icons/Truck";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import FloatingTooltip, { FloatingTooltipStyles } from "@vanmeter/App/Components/FloatingTooltip/FloatingTooltip";
import HeaderClickable, { HeaderClickableStyles } from "@vanmeter/App/Components/Header/HeaderClickable";
import MiniCart from "@vanmeter/App/Components/MiniCart/MiniCart";
import { CustomAppState } from "@vanmeter/App/Store/Reducers";
import { xIsAuthenticated } from "@vanmeter/App/Store/Selectors/GeneralSelectors";
import IconCart from "@vanmeter/XDL/Icons/IconCart";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: CustomAppState) => ({
    shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
    totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
    // XNG-Changes
    isTabletScreen: state.xCommon.isTabletScreen,
    isAuthenticated: xIsAuthenticated(state),
    allowGuestCheckout: getSettingsCollection(state).accountSettings.allowGuestCheckout,
});

const mapDispatchToProps = {
    loadCurrentCart,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
    // XNG-Changes
    headerClickable?: HeaderClickableStyles;
    xngTooltip?: FloatingTooltipStyles;
}

export const cartLinkStyles: CartLinkStyles = {
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
        },
        icon: {
            iconProps: {
                size: 24,
            },
        },
        color: "secondary.contrast",
        css: css`
            height: 50px;
            padding: 0 4px 0 10px;
        `,
    },
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
};

const iconMap = {
    cart: ShoppingCart,
    truck: Truck,
    bag: ShoppingBag,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    loadCurrentCart,
    // XNG-Changes
    isTabletScreen,
    isAuthenticated,
    allowGuestCheckout,
}) => {
    // XNG-Changes
    const [isMenuOpen, setMenuOpen] = useState(false);
    const { isEditing } = useShellContext();

    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false });
        }
    }, [shouldLoadCart]);

    // XNG-Changes
    if (!isAuthenticated && !isEditing && !allowGuestCheckout) {
        return null;
    }

    const styles = useMergeStyles("cartLink", cartLinkStyles);

    // XNG-Changes: add isTabletScreen and totalCountDisplay
    const showIcon = visibilityState !== "label" && !(isTabletScreen && !!totalCountDisplay);
    const showLabel = visibilityState !== "icon" && !isTabletScreen;

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const { routerLink } = styles;

    // XNG-Changes
    const displayCount = (retainedTotalCountToDisplay ?? 0) > 99 ? "99+" : retainedTotalCountToDisplay;

    // if (isCompactHeaderCart) {
    //     return (
    //         <Link
    //             {...routerLink}
    //             color={theme.header.linkColor}
    //             href={cartUrl}
    //             icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
    //             data-test-selector="cartLink"
    //         >
    //             <Typography as="span" {...styles.compactCountText}>
    //                 {retainedTotalCountToDisplay}
    //             </Typography>
    //             <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
    //         </Link>
    //     );
    // }
    return (
        <FloatingTooltip
            onOpenChange={open => setMenuOpen(open)}
            offset={0}
            placement="bottom-end"
            showArrow={false}
            triggerComponent={
                <HeaderClickable
                    isOpen={isMenuOpen}
                    href={cartUrl}
                    icon={{ src: IconCart, width: isTabletScreen ? 22 : 28 }}
                    data-test-selector="cartLink"
                    aria-label={translate("Mini-Cart")}
                    extendedStyles={styles.headerClickable}
                    showIcon={showIcon}
                    {...{ hasItems: !!totalCountDisplay }}
                >
                    {!!displayCount && <Typography className="totalCount">{displayCount}</Typography>}
                    {showLabel && <Typography className="totalLabel"> {translate("Cart")}</Typography>}
                </HeaderClickable>
            }
            enabled={!isTabletScreen}
            extendedStyles={styles.xngTooltip}
            overlay={true}
            isOpen={isMenuOpen}
        >
            <MiniCart isPopupShown={isMenuOpen} onClose={() => setMenuOpen(false)} />
        </FloatingTooltip>
    );
    // return (
    //     <Link
    //         {...routerLink}
    //         href={cartUrl}
    //         icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
    //         data-test-selector="cartLink"
    //     >
    //         <Hidden below="lg" as="span">
    //             {showLabel && `${translate("Cart")} `}(
    //             <Typography as="span" {...styles.desktopCountText} data-test-selector="cartLinkQuantity">
    //                 {retainedTotalCountToDisplay}
    //             </Typography>
    //             )
    //         </Hidden>
    //         <Hidden above="md">
    //             <Typography as="span" {...styles.mobileCountText}>
    //                 {retainedTotalCountToDisplay}
    //             </Typography>
    //         </Hidden>
    //         <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
    //     </Link>
    // );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
                // XNG-Changes
                isVisible: _ => false,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
